import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Activity, Brain, Wifi, TrendingUp } from 'lucide-react';

interface PatientMonitor {
  id: string;
  name: string;
  location: string;
  status: 'normal' | 'monitoring' | 'critical';
  lastUpdate: string;
  seizureRisk: number;
  signalQuality: number;
}

const MOCK_PATIENTS: PatientMonitor[] = [
  { id: 'P-001', name: 'Bed 12 - ICU', location: 'ICU', status: 'normal', lastUpdate: '2s ago', seizureRisk: 0.12, signalQuality: 0.95 },
  { id: 'P-002', name: 'Bed 5 - NICU', location: 'NICU', status: 'monitoring', lastUpdate: '5s ago', seizureRisk: 0.34, signalQuality: 0.88 },
  { id: 'P-003', name: 'Bed 8 - ICU', location: 'ICU', status: 'critical', lastUpdate: '1s ago', seizureRisk: 0.87, signalQuality: 0.72 },
];

export function NeuroCommandCenter() {
  const [patients] = useState(MOCK_PATIENTS);
  const [selectedPatient, setSelectedPatient] = useState<PatientMonitor>(patients[0]);

  return (
    <div className="p-6 space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Brain className="h-8 w-8 text-purple-500" />
            Neuro Command Center
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Live EEG monitoring - AI-powered seizure detection - Connectivity analysis
          </p>
        </div>
        <div className="flex gap-2">
          <Badge variant="outline" className="gap-1">
            <Activity className="h-3 w-3" />
            {patients.length} active
          </Badge>
        </div>
      </header>

      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Active EEG</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{patients.length}</div>
            <div className="text-xs text-muted-foreground">patients</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Critical</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">
              {patients.filter(p => p.status === 'critical').length}
            </div>
            <div className="text-xs text-muted-foreground">alerts</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Avg Signal</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">
              {Math.round(patients.reduce((s, p) => s + p.signalQuality, 0) / patients.length * 100)}%
            </div>
            <div className="text-xs text-muted-foreground">quality</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Models</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">v10.0</div>
            <div className="text-xs text-muted-foreground">AI inference</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Patients Under Monitoring</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {patients.map((patient) => {
              const dotClass = patient.status === 'critical'
                ? 'bg-red-500 animate-pulse'
                : patient.status === 'monitoring'
                ? 'bg-yellow-500'
                : 'bg-green-500';
              const rowClass = selectedPatient.id === patient.id ? 'bg-accent' : '';
              const riskClass = patient.seizureRisk > 0.7
                ? 'text-red-500'
                : patient.seizureRisk > 0.3
                ? 'text-yellow-500'
                : 'text-green-500';
              return (
                <div
                  key={patient.id}
                  onClick={() => setSelectedPatient(patient)}
                  className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:bg-accent ${rowClass}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${dotClass}`} />
                    <div>
                      <div className="font-medium">{patient.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {patient.id} - {patient.location} - {patient.lastUpdate}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Seizure: </span>
                      <span className={`font-medium ${riskClass}`}>
                        {Math.round(patient.seizureRisk * 100)}%
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Quality: </span>
                      <span className="font-medium">{Math.round(patient.signalQuality * 100)}%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wifi className="h-4 w-4" />
              Brain Network
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground">
              Connectivity matrix (placeholder). Real implementation renders D3 force-directed
              graph of brain region connectivity based on wPLI / PLV / coherence in selected
              frequency band.
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Cognitive Scores
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Attention:</span><span className="font-medium">--</span>
              </div>
              <div className="flex justify-between">
                <span>Workload:</span><span className="font-medium">--</span>
              </div>
              <div className="flex justify-between">
                <span>Fatigue:</span><span className="font-medium">--</span>
              </div>
              <div className="flex justify-between">
                <span>Sedation Index:</span><span className="font-medium">--</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Connects to AI inference service (HTTP) or uses local heuristic.
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="text-xs text-muted-foreground border-l-4 border-yellow-500 pl-3 py-1">
        Foundation view. AI inference, live streaming, and clinical alerts require trained
        models + device integrations (see docs/ministry/16-EEG-NEUROPHYSIOLOGY-ARCHITECTURE.md).
      </div>
    </div>
  );
}

export default NeuroCommandCenter;